import { AxiosError } from 'axios';
import __isArray from 'lodash/isArray';
import __isObject from 'lodash/isObject';
import __mapValues from 'lodash/mapValues';
/**
 * Parse form errors
 * @param data - The data to parse
 * @returns The parsed errors
 */
export function parseFormErrors(data) {
    return __mapValues(data, group => {
        if (__isArray(group) && group.length > 0) {
            const tmp = group[0];
            if (__isObject(tmp)) {
                // A) data.assignments = [{key: [error]}] - group = assignments
                return group.map(field => {
                    return __mapValues(field, innerGroup => {
                        if (__isArray(innerGroup) && innerGroup.length > 0) {
                            return innerGroup[0];
                        }
                        // create payment: data.notificationEmails: [{ emails: {0: [error]}}]
                        if (__isObject(innerGroup)) {
                            return Object.values(innerGroup);
                        }
                        return innerGroup;
                    });
                });
            }
            // B) data.assignments = [error]
            return tmp;
        }
        // C) data = {_error: errorMsg} - group = errorMsg
        return group;
    });
}
/**
 * Make a flat object from nested errors
 * @param errors - The errors to parse
 * @param parentKey - The parent key
 * @param result - The result object
 * @returns The parsed errors
 */
export function parseBackendAPIErrors(
// It can literally be anything, so we need to disable the type check
// eslint-disable-next-line @typescript-eslint/no-explicit-any
errors, parentKey = '', result = {}) {
    if (!errors) {
        return result;
    }
    Object.keys(errors).forEach(key => {
        const value = errors[key];
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        if (__isArray(value)) {
            const tmp = value[0];
            if (__isObject(tmp)) {
                value.forEach((item, index) => {
                    parseBackendAPIErrors(item, `${newKey}[${index}]`, result);
                });
            }
        }
        else if (typeof value === 'object' && value !== null) {
            parseBackendAPIErrors(value, newKey, result);
        }
        else {
            result[newKey] = value;
        }
    });
    return result;
}
/**
 * Parse initial errors for react-hook-form
 * @param initialErrors - The initial errors to parse
 * @returns The parsed errors
 */
export function parseRHFInitialErrors(initialErrors) {
    if (!initialErrors) {
        return null;
    }
    const formErrors = parseFormErrors(initialErrors);
    return parseBackendAPIErrors(formErrors);
}
/**
 * Function to check if the response is a version agnostic backend error response
 * @param response - The response to check
 * @returns True if the response is a version agnostic backend error response, false otherwise
 */
function isVersionAgnosticBackendErrorResponseGuard(response) {
    return (typeof response === 'object' &&
        response !== null &&
        'data' in response &&
        response.data !== undefined);
}
/**
 * Function to check if the error is a background job error
 * @param error - The error to check
 * @returns True if the error is a background job error, false otherwise
 */
function isBackgroundJobErrorGuard(error) {
    return (typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        error.message !== undefined);
}
/**
 * Function to get error message from data. Handles both v1 ("_error") and v2 ("Error") api errors.
 * @param data - The data to get the error message from
 * @returns The error message
 */
export function getErrorMessageFromData(data) {
    return data?.['_error'] || data?.['non_field_errors'] || data?.Error || data?.nonFieldErrors || null;
}
/**
 * Placeholder for network error message
 */
export const NETWORK_ERROR_MESSAGE_PLACEHOLDER = '### network error ###';
/**
 * Function to get common error message
 * @param error - The error to get the error message from
 * @returns The error message
 */
export function getCommonErrorMessage(error) {
    if (error instanceof AxiosError) {
        const { code, message, response, status } = error;
        if (response && isVersionAgnosticBackendErrorResponseGuard(response)) {
            const { data } = response;
            const responseErrorData = getErrorMessageFromData(data);
            if (responseErrorData) {
                return responseErrorData;
            }
        }
        // axios: network | server | timeout errors
        if (code === 'ERR_NETWORK' || (status && status >= 500)) {
            return NETWORK_ERROR_MESSAGE_PLACEHOLDER;
        }
        return message;
    }
    if (error instanceof Error) {
        return error.message;
    }
    // background job error result
    if (isBackgroundJobErrorGuard(error)) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'Unknown error';
}
/**
 * Function to get common form errors
 * @param error - The error to get the form errors from
 * @returns The form errors
 */
export function getCommonFormErrors(error) {
    if (error instanceof AxiosError) {
        const { response } = error;
        if (response && isVersionAgnosticBackendErrorResponseGuard(response)) {
            const { data, status } = response;
            if (status === 400) {
                return parseFormErrors(data);
            }
        }
    }
    const errorMsg = getCommonErrorMessage(error);
    return { _error: errorMsg };
}
